<template>
  <div class="btn-group c-dropdown" :class="{ open: isOpen }" v-click-outside="close">
    <button
      ref="trigger"
      class="dropdown-toggle btn"
      :class="[sizeClass, variantClass]"
      @click="toggle"
      aria-haspopup="true"
      :aria-expanded="isOpen"

    >
      <slot name="trigger" :selected="selected">
        <span class="label-text">
          {{ currentLabel }}
        </span>
        <i class="fa-regular fa-angle-down ml-half icon-down"></i>
      </slot>
    </button>

    <ul
      ref="menu"
      class="c-dropdown-menu dropdown-menu bs-dropdown--menu"
      :style="menuStyle"
    >
      <slot :close="close">
        <template v-if="items.length">
          <li v-for="item in items" :key="itemKey(item)" :class="{ active: isSelected(item) }">
            <a
              href="#"
              :title="itemLabel(item)"
              @click.prevent="select(item)"
            >

              <slot name="item" :item="item" :selected="isSelected(item)">
                {{ itemLabel(item) }}
              </slot>
            </a>
          </li>
        </template>
        <li v-else class="dropdown-empty">
          <span>{{ emptyText }}</span>
        </li>
      </slot>
    </ul>
  </div>
</template>

<script>
export default {
  name: "BsDropdown",

  model: {
    prop: "value",
    event: "input"
  },

  props: {
    value: {
      default: null
    },
    label: {
      type: String,
      default: "Dropdown"
    },
    items: {
      type: Array,
      default: function() {
        return [];
      }
    },
    itemKeyField: {
      type: String,
      default: "id"
    },
    itemLabelField: {
      type: String,
      default: "name"
    },
    variant: {
      type: String,
      default: "default"
    },
    size: {
      type: String,
      default: ""
    },
    emptyText: {
      type: String,
      default: "No items"
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      type: Number,
      default: 300
    },
    edgeMargin: {
      type: Number,
      default: 8
    }
  },

  directives: {
    "click-outside": {
      bind: function(el, binding) {
        el._clickOutside = function(e) {
          if (!el.contains(e.target)) {
            binding.value();
          }
        };
        document.addEventListener("click", el._clickOutside);
      },
      unbind: function(el) {
        document.removeEventListener("click", el._clickOutside);
      }
    }
  },

  data: function() {
    return {
      isOpen: false,
      fixedStyle: null
    };
  },

  computed: {
    selected: function() {
      if (this.value === null || this.value === undefined) return null;
      var self = this;
      return this.items.find(function(item) {
        return self.itemKey(item) === self.itemKey(self.value);
      }) || null;
    },
    currentLabel: function() {
      return this.selected ? this.itemLabel(this.selected) : this.label;
    },
    variantClass: function() {
      return "btn-" + this.variant;
    },
    sizeClass: function() {
      return this.size ? "btn-" + this.size : "";
    },
    menuStyle: function() {
      if (!this.isOpen || !this.fixedStyle) return { display: "none" };
      return this.fixedStyle;
    }
  },

  methods: {
    toggle: function() {
      this.isOpen ? this.close() : this.open();
    },
    close: function() {
      this.isOpen = false;
      this.fixedStyle = null;
    },
    open: function() {
      this.isOpen = true;
      // show menu offscreen first to measure it
      this.fixedStyle = {
        display: "block",
        position: "fixed",
        visibility: "hidden",
        top: "0",
        left: "0",
        maxHeight: this.maxHeight + "px",
        overflowY: "auto",
        zIndex: 1050
      };
      this.$nextTick(this.reposition);
    },
    reposition: function() {
      var trigger = this.$refs.trigger;
      var menu = this.$refs.menu;
      if (!trigger || !menu) return;

      var tRect = trigger.getBoundingClientRect();
      var mW = menu.offsetWidth;
      var mH = menu.offsetHeight;
      var vw = window.innerWidth;
      var vh = window.innerHeight;
      var m = this.edgeMargin;

      // --- Horizontal ---
      var left = tRect.left;

      // If overflows right — pin right edge to viewport
      if (left + mW > vw - m) {
        left = vw - m - mW;
      }
      // If that pushed it past left edge — pin left edge to viewport
      if (left < m) {
        left = m;
      }

      // --- Vertical ---
      var top;
      var spaceBelow = vh - tRect.bottom;
      var spaceAbove = tRect.top;

      if (mH <= spaceBelow) {
        // Fits below
        top = tRect.bottom + 2;
      } else if (mH <= spaceAbove) {
        // Fits above
        top = tRect.top - mH - 2;
      } else {
        // Neither fits fully — pick bigger side
        top = spaceBelow >= spaceAbove
          ? tRect.bottom + 2
          : tRect.top - mH - 2;
      }

      this.fixedStyle = {
        display: "block",
        position: "fixed",
        top: top + "px",
        left: left + "px",
        maxHeight: this.maxHeight + "px",
        overflowY: "auto",
        visibility: "visible",
        zIndex: 1050
      };
    },
    select: function(item) {
      this.$emit("input", item);
      this.$emit("select", item);
      if (this.closeOnSelect) {
        this.close();
      }
    },
    isSelected: function(item) {
      return this.selected && this.itemKey(item) === this.itemKey(this.selected);
    },
    itemKey: function(item) {
      return typeof item === "object" ? item[this.itemKeyField] : item;
    },
    itemLabel: function(item) {
      return typeof item === "object" ? item[this.itemLabelField] : item;
    }
  }
};
</script>

<style lang="scss">

.c-dropdown {
  .bs-dropdown--menu {
    min-width: 160px;
  }

  .dropdown-toggle {
    position: relative;
    padding-right: 25px;


    .fa-angle-down {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
    }
  }


  .dropdown-toggle {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .c-dropdown-menu {
    box-shadow: 1px 3px 4px rgba(0, 0, 0, .1);
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    background: white;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      margin: 8px 0;
    }

    &::-webkit-scrollbar-thumb {
      background: #d0d0d0;
      border-radius: 4px;

      &:hover {
        background: #b0b0b0;
      }
    }

    > li.active > a,
    > li.active > a:hover,
    > li.active > a:focus,
    > li > a.active,
    > li > a.router-link-exact-active,
    > li > a.router-link-exact-active:hover,
    > li > a.router-link-exact-active:focus {
      background-color: #e8e8e8;
      color: #262626;
    }

    li {
      &:hover {

        a {
          background-color: #e8e8e8 !important;
          color: #262626 !important;
        }
      }
    }


  }
}

</style>